"use client";

/**
 * 首页「最近访问 / 常用功能」的本地偏好（2026-10-08）。
 *
 * ## 为什么放 localStorage 而不是建表
 *
 * 这是**单机单用户**的工具（本项目 uid 由 middleware 按 origin 生成，无账号体系），
 * 而访问频次是「使用行为」不是「业务数据」——它没有任何跨设备/跨用户的价值，
 * 却要付出建表 + 迁移 + 每路由写库的代价（写放大还会和 SQLite WAL 抢锁）。
 *
 * 更实际的理由：**没有埋点就等于没有数据**。真建表就得在每个页面挂埋点，
 * 而这个项目有 100+ 路由，漏一处就是静默缺失；localStorage 版本只需要
 * 在 GlobalHeader 一处记 pathname —— 覆盖面天然完整。
 *
 * ## 🔴 三个必须处理的现实问题
 *
 * ① **SSR 水合不匹配**：服务端没有 localStorage，若首屏就渲染「置顶项」，
 *    客户端首次渲染会与服务端 HTML 不一致 → React 警告 + 布局跳动。
 *    处方：`ready` 标志 + 挂载后才渲染偏好驱动的部分（骨架先占位）。
 *
 * ② **旧版本残留脏数据**：用户可能带着上一个版本的 key 进来，
 *    其中有已下线的模块 id。故 `sanitize` 丢弃未注册 id，而不是渲染成死链。
 *
 * ③ **配额/隐私模式抛错**：`localStorage.setItem` 在无痕模式与配额满时抛
 *    `QuotaExceededError`。所有读写包 try/catch，失败时**静默降级为内存态**，
 *    绝不让偏好功能把整页带崩。
 *
 * ## 🔴 移除（hidden）与置顶（pinned）是两件事，不能互相顶替
 *
 * 用户要的是「这个功能我不常用，从常用功能里拿掉」，而不是「把它排到后面」——
 * 置顶只能排序，**减不掉**入口，9 张卡永远全在。
 * 所以 hidden 是独立的减法集合：
 *   · 移除时必须**同时取消置顶**，否则置顶区会残留一张「已被移除」的卡
 *     （取交集时被丢掉，但 prefs 里留着脏 id，下次「加入」会莫名其妙回到置顶区）；
 *   · 加入 = 从 hidden 里删掉，回到 sort 决定的自然位置，不自动置顶。
 *
 * 用 `hidden` 的**黑名单**而不是 `visible` 白名单：模块注册表会持续增长，
 * 白名单意味着「新增模块对老用户默认不可见」——那是静默缺失；
 * 黑名单让新模块默认可见，只有用户亲手拿掉的才不显示。
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import { HOME_MODULES } from "./modules";
import type { HomeModuleId } from "./types";

const VISIT_KEY = "aicomic_home_visits_v1";
const PREF_KEY = "aicomic_home_prefs_v1";

/** 访问记录上限：再旧的记录没有参考价值，且无上限会撑爆配额。 */
const MAX_VISITS = 40;

export interface VisitRecord {
  /** 域内路径（含 locale 段），如 `/zh/studio` */
  path: string;
  count: number;
  /** ISO 串 */
  lastAt: string;
}

export type VisitSort = "recent" | "frequent" | "manual";

export interface HomePrefs {
  /** 置顶的模块 id，数组顺序即手动排序顺序 */
  pinned: HomeModuleId[];
  /**
   * 被**移除**出常用功能的模块 id（黑名单）。
   *
   * 与 `pinned` 独立：置顶是排序能力，减不掉入口；只有这里能让模块从面板消失。
   * 旧的 localStorage 里没有这个字段 → 一律按「什么都没移除」处理（见 sanitizeIds）。
   */
  hidden: HomeModuleId[];
  sort: VisitSort;
}

const DEFAULT_PREFS: HomePrefs = { pinned: [], hidden: [], sort: "recent" };

// ── 安全读写 ────────────────────────────────────────────────────

function readJson<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as T;
    return parsed ?? fallback;
  } catch {
    // 解析失败（脏 JSON / 被别的代码写坏）→ 用默认值，不阻塞渲染
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // 配额满 / 无痕模式：偏好降级为「本次会话有效」，不报错、不打断使用
  }
}

const KNOWN_IDS = new Set<string>(HOME_MODULES.map((m) => m.id));

/** 丢掉未注册 id（版本迭代后残留）与非字符串项。pinned / hidden 共用同一套清洗。 */
function sanitizeIds(raw: unknown): HomeModuleId[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: HomeModuleId[] = [];
  for (const item of raw) {
    if (typeof item !== "string" || !KNOWN_IDS.has(item) || seen.has(item)) continue;
    seen.add(item);
    out.push(item as HomeModuleId);
  }
  return out;
}

function sanitizeVisits(raw: unknown): VisitRecord[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter(
      (v): v is VisitRecord =>
        !!v && typeof v === "object" && typeof (v as VisitRecord).path === "string",
    )
    .map((v) => ({
      path: v.path,
      count: Number.isFinite(v.count) ? Math.max(0, Math.trunc(v.count)) : 0,
      lastAt: typeof v.lastAt === "string" ? v.lastAt : new Date(0).toISOString(),
    }));
}

// ── 访问记录 ────────────────────────────────────────────────────

/** 读访问记录（服务端/首屏返回默认空数组，避免水合不匹配）。 */
export function readVisits(): VisitRecord[] {
  return sanitizeVisits(readJson<unknown>(VISIT_KEY, []));
}

/**
 * 记一次访问。**在 GlobalHeader 挂一次**即可覆盖全站路由 ——
 * 埋点分散到各页面必然漏，漏了就是静默缺失（首页看不出原因，只显得"没人用"）。
 */
export function recordVisit(path: string): void {
  if (typeof window === "undefined") return;
  if (!path || path === "/") return;
  const list = readVisits().filter((v) => v.path !== path);
  const prev = readVisits().find((v) => v.path === path);
  list.unshift({
    path,
    count: (prev?.count ?? 0) + 1,
    lastAt: new Date().toISOString(),
  });
  writeJson(VISIT_KEY, list.slice(0, MAX_VISITS));
}

// ── Hook ────────────────────────────────────────────────────────

export function useHomePrefs() {
  const [prefs, setPrefs] = useState<HomePrefs>(DEFAULT_PREFS);
  const [visits, setVisits] = useState<VisitRecord[]>([]);
  /** SSR 与首次客户端渲染都是 false —— 用它占位，避免水合不匹配 */
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const raw = readJson<Record<string, unknown>>(PREF_KEY, {});
    setPrefs({
      pinned: sanitizeIds(raw?.pinned),
      // 旧版本没有 hidden 字段 → sanitizeIds(undefined) = []，等价「什么都没移除」。
      // 不 bump PREF_KEY 到 v2：那会把老用户的置顶/排序一起丢掉换一个空对象。
      hidden: sanitizeIds(raw?.hidden),
      sort:
        raw?.sort === "frequent" || raw?.sort === "manual" || raw?.sort === "recent"
          ? raw.sort
          : "recent",
    });
    setVisits(readVisits());
    setReady(true);
  }, []);

  const togglePin = useCallback((id: HomeModuleId) => {
    setPrefs((prev) => {
      const pinned = prev.pinned.includes(id)
        ? prev.pinned.filter((x) => x !== id)
        : [...prev.pinned, id];
      const next = { ...prev, pinned };
      writeJson(PREF_KEY, next);
      return next;
    });
  }, []);

  const setSort = useCallback((sort: VisitSort) => {
    setPrefs((prev) => {
      const next = { ...prev, sort };
      writeJson(PREF_KEY, next);
      return next;
    });
  }, []);

  /**
   * 从常用功能里**移除**（用户点卡片上的 ⊘）。
   *
   * 🔴 同时把它从 pinned 里摘掉：留着的话 prefs 里会有一条「既被移除又置顶」的
   * 自相矛盾记录 —— 渲染时被交集丢掉看不出问题，但下次「加入」时它会**直接
   * 出现在置顶区**，用户读到的是「我明明只是加回来，怎么跑到置顶去了」。
   */
  const hideModule = useCallback((id: HomeModuleId) => {
    setPrefs((prev) => {
      const next: HomePrefs = {
        ...prev,
        hidden: prev.hidden.includes(id) ? prev.hidden : [...prev.hidden, id],
        pinned: prev.pinned.filter((x) => x !== id),
      };
      writeJson(PREF_KEY, next);
      return next;
    });
  }, []);

  /** 加回常用功能（用户点「加入」）。回到 sort 决定的自然位置，不自动置顶。 */
  const showModule = useCallback((id: HomeModuleId) => {
    setPrefs((prev) => {
      if (!prev.hidden.includes(id)) return prev;
      const next: HomePrefs = { ...prev, hidden: prev.hidden.filter((x) => x !== id) };
      writeJson(PREF_KEY, next);
      return next;
    });
  }, []);

  /** 手动排序：把 id 移到目标位置（置顶区内部拖动/上下移的语义）。 */
  const movePin = useCallback((id: HomeModuleId, toIndex: number) => {
    setPrefs((prev) => {
      const from = prev.pinned.indexOf(id);
      if (from < 0) return prev;
      const pinned = [...prev.pinned];
      pinned.splice(from, 1);
      pinned.splice(Math.max(0, Math.min(toIndex, pinned.length)), 0, id);
      const next = { ...prev, pinned };
      writeJson(PREF_KEY, next);
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    const next: HomePrefs = { ...DEFAULT_PREFS };
    writeJson(PREF_KEY, next);
    setPrefs(next);
  }, []);

  return { prefs, visits, ready, togglePin, setSort, movePin, reset, hideModule, showModule };
}

/**
 * 把访问记录按模块归并。
 *
 * 匹配规则：记录的 path **前缀等于**模块 path，或模块 path 是它的前缀
 * （`/zh/project/abc` 归属「项目」模块 `/zh`）。
 * 🔴 用 `startsWith` 裸比较会把 `/zh/studio` 也算进「项目」（因为 `/zh` 是前缀），
 * 所以必须**取最长匹配**——一个页面只归属一个模块，重复计数会让频次排序失真。
 */
export function mergeVisitsByModule(
  visits: VisitRecord[],
  locale: string,
): Map<HomeModuleId, { count: number; lastAt: string }> {
  const out = new Map<HomeModuleId, { count: number; lastAt: string }>();
  const ranked = HOME_MODULES.map((m) => ({
    id: m.id,
    path: m.path.replace("{locale}", locale),
  })).sort((a, b) => b.path.length - a.path.length);

  for (const v of visits) {
    const hit = ranked.find((m) => v.path === m.path || v.path.startsWith(`${m.path}/`));
    if (!hit) continue;
    const prev = out.get(hit.id);
    if (prev) {
      prev.count += v.count;
      if (v.lastAt > prev.lastAt) prev.lastAt = v.lastAt;
    } else {
      out.set(hit.id, { count: v.count, lastAt: v.lastAt });
    }
  }
  return out;
}

/**
 * 排序后的模块列表：置顶在前（手动序），其余按当前 sort 策略。
 *
 * 🔴 `hidden` 是**减法**，必须在置顶之前生效：被移除的模块既不在 pinned 也不在 rest，
 *    只出现在 `hidden_modules`（面板「添加」区里用「加入」把它拿回来）。
 *    先过滤再排序（而不是排完再 filter），顺序与结果都等价，但少一次无用比较。
 */
export function useSortedModules(prefs: HomePrefs, visits: VisitRecord[], locale: string) {
  return useMemo(() => {
    const usage = mergeVisitsByModule(visits, locale);
    const isHidden = (id: HomeModuleId) => prefs.hidden.includes(id);
    const visible = HOME_MODULES.filter((m) => !isHidden(m.id));

    const pinned = prefs.pinned
      .map((id) => HOME_MODULES.find((m) => m.id === id))
      .filter((m): m is NonNullable<typeof m> => !!m && !isHidden(m.id));
    const rest = visible.filter((m) => !prefs.pinned.includes(m.id));

    const sortedRest = [...rest].sort((a, b) => {
      if (prefs.sort === "frequent") {
        const d = (usage.get(b.id)?.count ?? 0) - (usage.get(a.id)?.count ?? 0);
        if (d !== 0) return d;
      }
      if (prefs.sort === "recent" || prefs.sort === "frequent") {
        const d = (usage.get(b.id)?.lastAt ?? "").localeCompare(usage.get(a.id)?.lastAt ?? "");
        if (d !== 0) return d;
      }
      // 稳定兜底：按注册表原序（HOME_MODULES 的书写顺序），不引入随机
      return HOME_MODULES.indexOf(a) - HOME_MODULES.indexOf(b);
    });

    // 「已移除」区按注册表原序，不按 sort —— 它是一个**管理列表**，
    // 位置随使用频次漂移会让用户每次都要重新找那个要加回来的功能。
    const hidden = HOME_MODULES.filter((m) => isHidden(m.id));

    return { pinned, rest: sortedRest, usage, hidden };
  }, [prefs, visits, locale]);
}
